/** @jsxImportSource @emotion/react */
import { useState } from "react";
import {
  EuiEmptyPrompt,
  EuiSkeletonText,
  EuiMarkdownEditor,
  EuiMarkdownEditorProps,
  EuiMarkdownFormat,
  EuiMarkdownParseError,
  EuiPanel,
} from "@inscopix/ideas-eui";
import { ButtonPermissioned } from "components/ButtonPermissioned/ButtonPermissioned";
import { isNonNullish } from "utils/isNonNullish";
import { css } from "@emotion/react";

interface MarkdownEditorProps {
  height?: number | "full";
  edit: boolean;
  setEdit: (edit: boolean) => void;
  disableEdit?: boolean;
  initialValue: string;
  emptyPrompt: string;
  onSave: (markdown: string) => Promise<void>;
}

/**
 * Dataset description editor and display component
 */
export const MarkdownEditor = ({
  edit,
  setEdit,
  disableEdit,
  emptyPrompt,
  height = "full",
  initialValue,
  onSave,
}: MarkdownEditorProps) => {
  // validation messages for EuiMarkdownEditor
  const [messages, setMessages] = useState<EuiMarkdownParseError[]>([]);

  const [editorMarkdownValue, setEditorMarkdownValue] = useState(initialValue);

  const [loading, setLoading] = useState(false);

  // sets markdown validation errors
  const onParse: EuiMarkdownEditorProps["onParse"] = (err, { messages }) => {
    setMessages(isNonNullish(err) ? [err] : messages);
  };

  const handleSave = async () => {
    if (editorMarkdownValue === initialValue) {
      return setEdit(false);
    }
    setLoading(true);
    await onSave(editorMarkdownValue);
    setEdit(false);
    setLoading(false);
  };

  const cancelEditMarkdown = () => {
    setEdit(false);
    setEditorMarkdownValue(initialValue);
  };

  const body = (() => {
    if (loading) {
      return <EuiSkeletonText />;
    }

    if (edit) {
      return (
        <EuiMarkdownEditor
          aria-label="Markdown Editor"
          value={editorMarkdownValue}
          onChange={setEditorMarkdownValue}
          onSave={() => void handleSave()}
          onCancel={cancelEditMarkdown}
          height={height}
          onParse={onParse}
          errors={messages}
          css={css`
            .euiMarkdownEditorToolbar {
              > .euiFlexGroup {
                flex-wrap: wrap;
                gap: 0px;
                > .euiFlexItem {
                  flex-direction: row;
                }
              }
              .euiMarkdownEditorToolbar__buttons {
                flex-wrap: nowrap;
              }
            }
          `}
        />
      );
    }

    if (initialValue === "") {
      return (
        <EuiEmptyPrompt
          body={
            <>
              <p>{emptyPrompt}</p>
            </>
          }
          actions={
            disableEdit ? null : (
              <ButtonPermissioned
                onClick={() => setEdit(true)}
                size="s"
                color="primary"
                fill
                aria-label="edit markdown"
                requiredPermission="edit"
              >
                Edit
              </ButtonPermissioned>
            )
          }
        />
      );
    }

    return <EuiMarkdownFormat>{initialValue}</EuiMarkdownFormat>;
  })();

  return (
    <EuiPanel
      paddingSize="s"
      hasShadow={false}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {body}
    </EuiPanel>
  );
};
